import { badgeVariants } from "@/components/ui/badge"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Typography } from "@/components/ui/typography"
import {
  INQUIRY_VAR_TOOLTIP_KEYS,
  INQUIRY_VARS_BY_LANG,
} from "@/features/material-compliance/components/supplier-declarations/inquiry/draft/inquiryDraftMock"
import {
  RichTextArea,
  RichTextInput,
} from "@/features/material-compliance/components/supplier-declarations/inquiry/draft/RichTextArea"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { useRef, type DragEvent } from "react"
import type { ContentLanguage } from "../../../components/SettingsControls"

type CaretField = HTMLInputElement | HTMLTextAreaElement

/** Inserts `{{token}} ` at the caret, keeping focus and the caret after it. */
function insertAtCaret(
  field: CaretField | null,
  value: string,
  token: string,
  onChange: (next: string) => void,
) {
  const insertion = `${token} `
  const start = field?.selectionStart ?? value.length
  const end = field?.selectionEnd ?? value.length
  const caret = start + insertion.length
  onChange(value.slice(0, start) + insertion + value.slice(end))
  requestAnimationFrame(() => {
    field?.focus()
    field?.setSelectionRange(caret, caret)
  })
}

/** Lets a variable pill be dropped onto a field. */
function allowVariableDrop(event: DragEvent<CaretField>) {
  event.preventDefault()
  event.dataTransfer.dropEffect = "copy"
}

/** The variable a dropped pill carries, if any. */
function droppedVariable(event: DragEvent<CaretField>): string | null {
  event.preventDefault()
  return event.dataTransfer.getData("text/plain") || null
}

/**
 * The variables a template may use, as pills to click or drag in. Shown under
 * every field, so it also says which variables exist: the backend refuses
 * any other {{token}} on save.
 */
function VariablePalette({
  language,
  onInsert,
}: {
  language: ContentLanguage
  onInsert: (token: string) => void
}) {
  const { translate } = useTranslation()
  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-wrap items-center gap-2">
        <Typography as="muted">
          {translate("MC_INQ_DRAFT_VARS_LABEL")}
        </Typography>
        {INQUIRY_VARS_BY_LANG[language].map((token) => (
          <Tooltip key={token}>
            <TooltipTrigger asChild>
              <button
                type="button"
                draggable
                onDragStart={(event) => {
                  event.dataTransfer.setData("text/plain", token)
                  event.dataTransfer.effectAllowed = "copy"
                }}
                onClick={() => onInsert(token)}
                className={cn(
                  badgeVariants({ variant: "outline" }),
                  "cursor-grab active:cursor-grabbing",
                )}
              >
                {token}
              </button>
            </TooltipTrigger>
            <TooltipContent>
              {translate(INQUIRY_VAR_TOOLTIP_KEYS[token])}
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  )
}

/** One-line subject with the variable palette. */
export function TemplateSubjectField({
  id,
  value,
  language,
  disabled,
  onChange,
}: {
  id: string
  value: string
  language: ContentLanguage
  disabled?: boolean
  onChange: (next: string) => void
}) {
  const ref = useRef<HTMLInputElement>(null)
  const insert = (token: string) =>
    insertAtCaret(ref.current, value, token, onChange)
  return (
    <div className="flex flex-col gap-2">
      <RichTextInput
        id={id}
        ref={ref}
        value={value}
        disabled={disabled}
        onChange={onChange}
        // The settings inputs' box, with {{variables}} in yellow as in the
        // message below.
        className="h-9 rounded-md border border-input bg-input/30 shadow-xs focus-within:ring-1 focus-within:ring-ring"
        onDragOver={allowVariableDrop}
        onDrop={(event) => {
          const token = droppedVariable(event)
          if (token) insert(token)
        }}
      />
      {!disabled && <VariablePalette language={language} onInsert={insert} />}
    </div>
  )
}

/** Multi-line message with highlighted {{variables}} and the palette. */
export function TemplateBodyField({
  value,
  language,
  disabled,
  onChange,
}: {
  value: string
  language: ContentLanguage
  disabled?: boolean
  onChange: (next: string) => void
}) {
  const ref = useRef<HTMLTextAreaElement>(null)
  const insert = (token: string) =>
    insertAtCaret(ref.current, value, token, onChange)
  return (
    <div className="flex flex-col gap-2">
      <RichTextArea
        ref={ref}
        value={value}
        readOnly={disabled}
        onChange={onChange}
        rows={7}
        className="rounded-md bg-input/30"
        onDragOver={allowVariableDrop}
        onDrop={(event) => {
          const token = droppedVariable(event)
          if (token) insert(token)
        }}
      />
      {!disabled && <VariablePalette language={language} onInsert={insert} />}
    </div>
  )
}
